import {Switch as SwitchPrimitive} from '@base-ui/react/switch'
import {forwardRef, ReactNode} from 'react'
import {InputHelpText} from '@components/ui/input-help-text'
import {Label} from '@components/ui/label'
import {cn} from '@utilities/helpers'

interface SwitchProps {
    id: string
    className?: string
    label?: ReactNode
    labelPosition?: 'left' | 'right'
    disabled?: boolean
    defaultChecked?: boolean
    errorMessage?: string
    checked?: boolean
    onChange?: (checked: boolean) => void
    helpText?: string
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
    (
        {
            id,
            label,
            className,
            labelPosition = 'right',
            disabled,
            defaultChecked,
            checked,
            onChange,
            errorMessage,
            helpText
        },
        ref
    ) => {
        return (
            <div
                className={cn(
                    'flex cursor-pointer items-center gap-4 whitespace-nowrap',
                    labelPosition === 'right' ? 'flex-row' : 'flex-row-reverse',
                    className
                )}
            >
                <SwitchPrimitive.Root
                    checked={checked}
                    className={cn(
                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors',
                        'bg-neutral-300 data-checked:bg-primary-600',
                        'data-disabled:cursor-not-allowed data-disabled:bg-neutral-200 data-checked:data-disabled:bg-primary-400'
                    )}
                    data-slot="switch"
                    defaultChecked={defaultChecked}
                    disabled={disabled}
                    id={id}
                    onCheckedChange={onChange}
                    ref={ref}
                >
                    <SwitchPrimitive.Thumb
                        className="pointer-events-none block size-5 translate-x-0 rounded-full bg-white shadow-xs transition-transform data-checked:translate-x-5"
                        data-slot="switch-thumb"
                    />
                </SwitchPrimitive.Root>

                <div className="flex flex-col gap-1">
                    {label && <Label htmlFor={id}>{label}</Label>}
                    {(helpText || errorMessage) && <InputHelpText error={errorMessage} helpText={helpText} />}
                </div>
            </div>
        )
    }
)

Switch.displayName = 'Switch'
